import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import { validatePromotionContent, validateDateRange, BILINGUAL_REQUIRED } from '../../utils/formValidation';

/**
 * EnhancedPromotionForm - Promotion pack form with Input Quality Layer enhancements
 */
const EnhancedPromotionForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'promotion'
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  const requiresBilingual = BILINGUAL_REQUIRED[packType];

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* Campaign Name */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Campaign Name
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Internal campaign identifier. Example: "Ramadan 2025 Flash Sale"
        </div>

        <input
          type="text"
          value={content.campaign_name || ''}
          onChange={(e) => handleChange('campaign_name', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('campaign_name')}
          placeholder="e.g., Ramadan 2025 Flash Sale"
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.campaign_name && errors.content?.campaign_name ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
          }}
        />
        {touched.campaign_name && errors.content?.campaign_name && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.campaign_name}
          </div>
        )}
      </div>

      {/* Offer Details */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Offer Details
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Clear description of the promotion. Example: "Buy two bottles of Olivetin and get one free until 31 December. Customer loyalty campaign promoting heart health."
        </div>

        <textarea
          value={content.offer_details || ''}
          onChange={(e) => handleChange('offer_details', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('offer_details')}
          placeholder="e.g., Buy two bottles of Olivetin and get one free until 31 December. Perfect for family heart health. Limited to first 500 customers."
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.offer_details && errors.content?.offer_details ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
        {touched.offer_details && errors.content?.offer_details && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.offer_details}
          </div>
        )}
      </div>

      {/* Date Range */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: EMZI_SPACING.md,
      }}>
        {/* Start Date */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: EMZI_SPACING.xs,
            color: EMZI_COLORS.textPrimary,
          }}>
            Start Date
            <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
          </label>
          
          <input
            type="date"
            value={content.start_date || ''}
            onChange={(e) => handleChange('start_date', e.target.value)}
            onBlur={() => onFieldBlur && onFieldBlur('start_date')}
            style={{
              width: '100%',
              padding: EMZI_SPACING.sm,
              border: `1px solid ${touched.start_date && errors.content?.start_date ? EMZI_COLORS.error : EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              fontSize: '14px',
              outline: 'none',
            }}
          />
          {touched.start_date && errors.content?.start_date && (
            <div style={{
              marginTop: '4px',
              fontSize: '12px',
              color: EMZI_COLORS.error,
            }}>
              {errors.content.start_date}
            </div>
          )}
        </div>

        {/* End Date */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: EMZI_SPACING.xs,
            color: EMZI_COLORS.textPrimary,
          }}>
            End Date
            <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
          </label>
          
          <input
            type="date"
            value={content.end_date || ''}
            onChange={(e) => handleChange('end_date', e.target.value)}
            onBlur={() => onFieldBlur && onFieldBlur('end_date')}
            style={{
              width: '100%',
              padding: EMZI_SPACING.sm,
              border: `1px solid ${touched.end_date && errors.content?.end_date ? EMZI_COLORS.error : EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              fontSize: '14px',
              outline: 'none',
            }}
          />
          {touched.end_date && errors.content?.end_date && (
            <div style={{
              marginTop: '4px',
              fontSize: '12px',
              color: EMZI_COLORS.error,
            }}>
              {errors.content.end_date}
            </div>
          )}
        </div>
      </div>

      {/* Date range validation message */}
      {touched.start_date && touched.end_date && errors.content?.date_range && (
        <div style={{
          padding: EMZI_SPACING.sm,
          background: '#fef2f2',
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '12px',
          color: EMZI_COLORS.error,
          borderLeft: `3px solid ${EMZI_COLORS.error}`,
        }}>
          ⚠️ {errors.content.date_range}
        </div>
      )}

      {/* Promo Code - Optional */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Promo Code
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Unique code for customers to use. Example: "RAMADAN25"
        </div>

        <input
          type="text"
          value={content.promo_code || ''}
          onChange={(e) => handleChange('promo_code', e.target.value.toUpperCase())}
          placeholder="e.g., RAMADAN25"
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
            textTransform: 'uppercase',
          }}
        />
      </div>

      {/* Terms & Conditions */}
      <StructuredListInput
        label="Terms & Conditions"
        items={content.terms || []}
        onChange={(items) => handleChange('terms', items)}
        minItems={1}
        maxItems={15}
        placeholder="Add a term or condition..."
        helper="Legal terms and conditions. Example: 'Applicable only to online purchases via EMZI Care platform' or 'Limited to first 500 customers'"
        required={false}
        hasError={touched.terms && errors.content?.terms}
        errorMessage={errors.content?.terms}
      />

      {/* Eligible Products - Optional */}
      <StructuredListInput
        label="Eligible Products"
        items={content.eligible_products || []}
        onChange={(items) => handleChange('eligible_products', items)}
        minItems={0}
        maxItems={50}
        placeholder="Add a product SKU or name..."
        helper="Products included in this promotion. Example: 'OLIVE-500ML' or 'All Premium Olive Oil variants'"
        required={false}
        hasError={false}
      />

      {/* Relationship hint */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#fef3c7',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: '#92400e',
        borderLeft: `3px solid ${EMZI_COLORS.warning}`,
      }}>
        ⚠️ <strong>Remember:</strong> Link this promotion to at least one Product pack in the Relationships section to meet validation requirements.
      </div>
    </div>
  );
};

export default EnhancedPromotionForm;
